import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import "./Toast.css";

export type ToastVariant = "info" | "success" | "danger";

export interface ToastOptions {
  variant?: ToastVariant;
  /** Milliseconds before auto-dismiss. `0` keeps the toast until dismissed. */
  duration?: number;
  /** A second line under the message. */
  description?: string;
  action?: { label: string; onClick: () => void };
}

export interface ToastApi {
  /** Queues a toast and returns its id. */
  toast: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

interface ToastRecord {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
  description: string | undefined;
  action: { label: string; onClick: () => void } | undefined;
  exiting: boolean;
}

/** Never more than three on screen at once (DESIGN.md §7). */
const MAX_VISIBLE = 3;

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  info: 5000,
  success: 5000,
  // Errors need longer than the eye takes to reach the corner.
  danger: 8000,
};

function readDurationToken(token: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value)) return fallback;
  return raw.endsWith("ms") ? value : value * 1000;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (value === null) {
    throw new Error("useToast must be used inside a <ToastProvider>.");
  }
  return value;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Overrides the per-variant defaults for every toast in the tree. */
  defaultDuration?: number;
  /** Label for the live region that holds the stack. */
  regionLabel?: string;
}

export function ToastProvider({
  children,
  defaultDuration,
  regionLabel = "Notifications",
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(0);
  const exitTimers = useRef(new Map<string, number>());

  const remove = useCallback((id: string) => {
    setToasts((current) => current.filter((entry) => entry.id !== id));
    exitTimers.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      if (exitTimers.current.has(id)) return;
      setToasts((current) =>
        current.map((entry) =>
          entry.id === id ? { ...entry, exiting: true } : entry,
        ),
      );
      const timer = window.setTimeout(
        () => remove(id),
        readDurationToken("--fc-duration-quick", 140),
      );
      exitTimers.current.set(id, timer);
    },
    [remove],
  );

  const toast = useCallback(
    (message: string, options?: ToastOptions) => {
      nextId.current += 1;
      const id = `fc-toast-${String(nextId.current)}`;
      const variant = options?.variant ?? "info";

      const record: ToastRecord = {
        id,
        message,
        variant,
        duration:
          options?.duration ?? defaultDuration ?? DEFAULT_DURATIONS[variant],
        description: options?.description,
        action: options?.action,
        exiting: false,
      };

      // Newest wins: the oldest still-live toast is dropped past the cap.
      setToasts((current) => {
        const live = current.filter((entry) => !entry.exiting);
        const overflow = Math.max(0, live.length + 1 - MAX_VISIBLE);
        const dropped = new Set(
          live.slice(0, overflow).map((entry) => entry.id),
        );
        return [...current.filter((entry) => !dropped.has(entry.id)), record];
      });

      return id;
    },
    [defaultDuration],
  );

  useEffect(() => {
    const timers = exitTimers.current;
    return () => {
      for (const timer of timers.values()) window.clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== "undefined"
        ? createPortal(
            <div
              className="fc-toast-region"
              role="region"
              aria-label={regionLabel}
            >
              {toasts.map((entry) => (
                <ToastItem key={entry.id} toast={entry} onDismiss={dismiss} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  toast: ToastRecord;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [paused, setPaused] = useState(false);
  const remaining = useRef(toast.duration);
  const startedAt = useRef(0);

  // The timer restarts on resume with whatever time was left when it paused.
  useEffect(() => {
    if (toast.duration <= 0 || paused || toast.exiting) return undefined;

    startedAt.current = Date.now();
    const timer = window.setTimeout(
      () => onDismiss(toast.id),
      remaining.current,
    );

    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(
        0,
        remaining.current - (Date.now() - startedAt.current),
      );
    };
  }, [paused, toast.duration, toast.exiting, toast.id, onDismiss]);

  const classes = [
    "fc-toast",
    `fc-toast--${toast.variant}`,
    toast.exiting ? "is-exiting" : "is-entering",
  ].join(" ");

  return (
    <div
      className={classes}
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <span className="fc-toast__icon" aria-hidden="true">
        <ToastGlyph variant={toast.variant} />
      </span>

      <div className="fc-toast__content">
        <p className="fc-toast__message">{toast.message}</p>
        {toast.description !== undefined ? (
          <p className="fc-toast__description">{toast.description}</p>
        ) : null}
      </div>

      {toast.action !== undefined ? (
        <button
          type="button"
          className="fc-toast__action"
          onClick={() => {
            toast.action?.onClick();
            onDismiss(toast.id);
          }}
        >
          {toast.action.label}
        </button>
      ) : null}

      <button
        type="button"
        className="fc-toast__close"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
      >
        <svg
          viewBox="0 0 16 16"
          width="16"
          height="16"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M4 4l8 8M12 4l-8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

function ToastGlyph({ variant }: { variant: ToastVariant }) {
  if (variant === "success") {
    return (
      <svg
        viewBox="0 0 16 16"
        width="16"
        height="16"
        aria-hidden="true"
        focusable="false"
      >
        <circle
          cx="8"
          cy="8"
          r="6.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M5.2 8.2 7.1 10.1 10.8 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (variant === "danger") {
    return (
      <svg
        viewBox="0 0 16 16"
        width="16"
        height="16"
        aria-hidden="true"
        focusable="false"
      >
        <circle
          cx="8"
          cy="8"
          r="6.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M8 4.75v4M8 11.1v.15"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 7.4v4M8 4.75v.15"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
